import { lazy, FC, Suspense } from 'react'
import { Route, Routes, Navigate } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { DashboardPage } from '../pages/dashboard/DashboardWrapper'
import { JsonAktarPage } from '../pages/jsonaktar/JsonAktar'
import CreateRequest from '../pages/jsonaktar/CreateRequest'
import CreatePackages from '../pages/jsonaktar/CreatePackages'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { DisableSidebar } from '../../_metronic/layout/core'
import { WithChildren } from '../../_metronic/helpers'

const PrivateRoutes = () => {
    const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
    const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'))
    const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
    const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'))
    const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))
    const ModelListPage = lazy(() => import('../pages/modellist/Page'))
    const MethodListPage = lazy(() => import('../pages/methodlist/Page'))
    const RoleMethodListPage = lazy(() => import('../pages/rolemethodlist/Page'))
    const RoleModelListPage = lazy(() => import('../pages/rolemodellist/Page'))
    const MenuListPage = lazy(() => import('../pages/menulist/Page'))
    const MenuRolePage = lazy(() => import('../pages/menurole/Page'))
    const UserActionPage = lazy(() => import('../pages/useraction/Page'))
    const CulturePage = lazy(() => import('../pages/culture/Page'))
    const TranslationPage = lazy(() => import('../pages/translation/Page'))
    const ApplicationUserPage = lazy(() => import('../pages/applicationuser/Page'))
    const ApplicationRolePage = lazy(() => import('../pages/applicationrole/Page'))
    const ApplicationUserRolePage = lazy(() => import('../pages/applicationuserrole/Page'))
    const ApplicationUserClaimPage = lazy(() => import('../pages/applicationuserclaim/Page'))
    const ApplicationRoleClaimPage = lazy(() => import('../pages/applicationroleclaim/Page'))
    const TenantPage = lazy(() => import('../pages/tenant/Page'))
    const QuestionPage = lazy(() => import('../pages/question/Page'))
    const QuestionPreviewPage = lazy(() => import('../pages/question/Preview'))
    const MyQuestionPreviewPage = lazy(() => import('../pages/my-question/Preview'))
    const QuestionSearchPage = lazy(() => import('../pages/question/Search'))
    const QuestionStatusPage = lazy(() => import('../pages/questionstatus/Page'))
    const TopicPage = lazy(() => import('../pages/topic/Page'))
    const QuestionReviewPage = lazy(() => import('../pages/questionreview/Page'))
    const PackagePage = lazy(() => import('../pages/package/Page'))
    const MyPackagePage = lazy(() => import('../pages/my-package/Page'))
    const MyControlPage = lazy(() => import('../pages/my-control/Page'))
    const MyQuestionPage = lazy(() => import('../pages/my-question/Page'))
    const MyQuestionControlPage = lazy(() => import('../pages/my-question-control/Page'))
    const MyReturnedQuestionPage = lazy(() => import('../pages/my-returned-question/Page'))
    const TitlePage = lazy(() => import('../pages/title/Page'))
    const RequestPage = lazy(() => import('../pages/request/Page'))
    const RequestArchivePage = lazy(() => import('../pages/request-archive/Page'))
    const ActiveRequestPage = lazy(() => import('../pages/activerequest/Page'))
    const UserTopicExpertisePage = lazy(() => import('../pages/usertopicexpertise/Page'))
    const ReportsPage = lazy(() => import('../pages/reports/Page'))
    const MyObjectionsPage = lazy(() => import('../pages/my-objections/Page'))
    const SettingPage = lazy(() => import('../pages/setting/Page'))

    return (
        <Routes>

            <Route element={<MasterLayout />}>
                {/* Redirect to Dashboard after success login/registartion */}
                <Route path='auth/*' element={<Navigate to='/' />} />

                <Route path='dashboard' element={<DashboardPage />} />
                <Route path='jsonaktar' element={<JsonAktarPage />} />
                <Route path='create-request' element={<CreateRequest />} />
                <Route path='create-packages' element={<CreatePackages />} />
                <Route
                    path='reports'
                    element={
                        <SuspensedView>
                            <ReportsPage />
                        </SuspensedView>
                    }
                />

                {/* Lazy Modules */}
                <Route
                    path='crafted/pages/profile/*'
                    element={
                        <SuspensedView>
                            <ProfilePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='crafted/pages/wizards/*'
                    element={
                        <SuspensedView>
                            <WizardsPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='crafted/widgets/*'
                    element={
                        <SuspensedView>
                            <WidgetsPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='crafted/account/*'
                    element={
                        <SuspensedView>
                            <AccountPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='apps/chat/*'
                    element={
                        <SuspensedView>
                            <ChatPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='modellist/*'
                    element={
                        <SuspensedView>
                            <ModelListPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='methodlist/*'
                    element={
                        <SuspensedView>
                            <MethodListPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='rolemethodlist/*'
                    element={
                        <SuspensedView>
                            <RoleMethodListPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='rolemodellist/*'
                    element={
                        <SuspensedView>
                            <RoleModelListPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='menulist/*'
                    element={
                        <SuspensedView>
                            <MenuListPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='menurole/*'
                    element={
                        <SuspensedView>
                            <MenuRolePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='useraction/*'
                    element={
                        <SuspensedView>
                            <UserActionPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='culture/*'
                    element={
                        <SuspensedView>
                            <CulturePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='translation/*'
                    element={
                        <SuspensedView>
                            <TranslationPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='applicationuser/*'
                    element={
                        <SuspensedView>
                            <ApplicationUserPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='applicationrole/*'
                    element={
                        <SuspensedView>
                            <ApplicationRolePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='previewpackage'
                    element={
                        <SuspensedView>
                            <QuestionPreviewPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='searchquestion'
                    element={
                        <SuspensedView>
                            <QuestionSearchPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='my-previewpackage'
                    element={
                        <SuspensedView>
                            <MyQuestionPreviewPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='applicationuserrole/*'
                    element={
                        <SuspensedView>
                            <ApplicationUserRolePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='applicationuserclaim/*'
                    element={
                        <SuspensedView>
                            <ApplicationUserClaimPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='applicationroleclaim/*'
                    element={
                        <SuspensedView>
                            <ApplicationRoleClaimPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='tenant/*'
                    element={
                        <SuspensedView>
                            <TenantPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='question/*'
                    element={
                        <SuspensedView>
                            <QuestionPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='questionstatus/*'
                    element={
                        <SuspensedView>
                            <QuestionStatusPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='topic/*'
                    element={
                        <SuspensedView>
                            <TopicPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='questionreview/*'
                    element={
                        <SuspensedView>
                            <QuestionReviewPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='package/*'
                    element={
                        <SuspensedView>
                            <PackagePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='my-package/*'
                    element={
                        <SuspensedView>
                            <MyPackagePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='my-control/*'
                    element={
                        <SuspensedView>
                            <MyControlPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='my-question/*'
                    element={
                        <SuspensedView>
                            <MyQuestionPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='my-question-control/*'
                    element={
                        <SuspensedView>
                            <MyQuestionControlPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='my-returned-question/*'
                    element={
                        <SuspensedView>
                            <MyReturnedQuestionPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='my-objections/*'
                    element={
                        <SuspensedView>
                            <MyObjectionsPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='request/*'
                    element={
                        <SuspensedView>
                            <RequestPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='request-archive/*'
                    element={
                        <SuspensedView>
                            <RequestArchivePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='activerequest/*'
                    element={
                        <SuspensedView>
                            <ActiveRequestPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='usertopicexpertise/*'
                    element={
                        <SuspensedView>
                            <UserTopicExpertisePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='setting/*'
                    element={
                        <SuspensedView>
                            <SettingPage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='title/*'
                    element={
                        <SuspensedView>
                            <TitlePage />
                        </SuspensedView>
                    }
                />
                <Route
                    path='dashboard/*'
                    element={
                        <SuspensedView>
                            <DashboardPage />
                        </SuspensedView>
                    }
                />
                <Route path='*' element={<Navigate to='/error/404' />} />
            </Route>
        </Routes>
    )
}

const SuspensedView: FC<WithChildren> = ({ children }) => {
    const baseColor = getCSSVariableValue('--bs-primary')
    TopBarProgress.config({
        barColors: {
            '0': baseColor,
        },
        barThickness: 1,
        shadowBlur: 5,
    })
    return (
        <Suspense fallback={<TopBarProgress />}>
            <DisableSidebar>{children}</DisableSidebar>
        </Suspense>
    )
}

export { PrivateRoutes }
